import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ThermostatRoundedIcon from '@mui/icons-material/ThermostatRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import CellTowerRoundedIcon from '@mui/icons-material/CellTowerRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';

const TILE_SIZE = 256;
const MIN_LAT = -85.0511;
const MAX_LAT = 85.0511;
const MIN_LON = -180;
const MAX_LON = 180;
const BUILDING_COLOR = '#2563EB';
const USPD_COLOR = '#D97706';
const BASE_STATION_COLOR = '#059669';
const COVERAGE_COLORS = ['#0284C7', '#059669', '#D97706', '#DC2626', '#7C3AED', '#0F766E'];

const stableColor = (value) => {
  const hash = String(value || '').split('').reduce((sum, char) => ((sum * 31) + char.charCodeAt(0)) >>> 0, 0);
  return COVERAGE_COLORS[hash % COVERAGE_COLORS.length];
};

const convexHull = (points) => {
  if (points.length <= 2) return points;
  const sorted = [...points].sort((a, b) => (a.x - b.x) || (a.y - b.y));
  const cross = (origin, a, b) =>
    ((a.x - origin.x) * (b.y - origin.y)) - ((a.y - origin.y) * (b.x - origin.x));
  const lower = [];
  sorted.forEach((point) => {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0) {
      lower.pop();
    }
    lower.push(point);
  });
  const upper = [];
  [...sorted].reverse().forEach((point) => {
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0) {
      upper.pop();
    }
    upper.push(point);
  });
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
};

const bufferedHull = (points, padding = 20) => {
  if (!points.length) return [];
  const cloud = points.flatMap((point) =>
    Array.from({ length: 12 }, (_unused, index) => {
      const angle = (Math.PI * 2 * index) / 12;
      return {
        x: point.x + Math.cos(angle) * padding,
        y: point.y + Math.sin(angle) * padding,
      };
    })
  );
  return convexHull(cloud);
};

const curvedPath = (from, to) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  const bend = Math.min(26, length * 0.08);
  const controlX = (from.x + to.x) / 2 - (dy / length) * bend;
  const controlY = (from.y + to.y) / 2 + (dx / length) * bend;
  return `M ${from.x} ${from.y} Q ${controlX} ${controlY} ${to.x} ${to.y}`;
};

const markerMeta = (kind) => {
  if (kind === 'uspd') {
    return { color: USPD_COLOR, label: 'УСПД', Icon: RouterRoundedIcon };
  }
  if (kind === 'base_station') {
    return { color: BASE_STATION_COLOR, label: 'Базовая станция', Icon: CellTowerRoundedIcon };
  }
  if (kind === 'mixed') {
    return { color: '#334155', label: 'Несколько объектов', Icon: LayersRoundedIcon };
  }
  return { color: BUILDING_COLOR, label: 'Дом', Icon: ApartmentRoundedIcon };
};

const statusMeta = (status) => {
  if (status === 'UP') return { label: 'Доступен', color: '#15803D' };
  if (status === 'DEGRADED') return { label: 'Нестабильно', color: '#B45309' };
  if (status === 'DOWN') return { label: 'Недоступен', color: '#DC2626' };
  return { label: 'Нет данных', color: '#64748B' };
};

const readingStatusMeta = (state) => {
  if (state === 'complete') return { label: 'Все показания получены', color: '#15803D', background: '#F0FDF4' };
  if (state === 'partial') return { label: 'Собрано частично', color: '#B45309', background: '#FFFBEB' };
  if (state === 'no_data') return { label: 'Показаний нет', color: '#B91C1C', background: '#FEF2F2' };
  if (state === 'no_devices') return { label: 'Приборы не установлены', color: '#475569', background: '#F1F5F9' };
  if (state === 'before_start') return { label: 'Учёт ещё не начат', color: '#64748B', background: '#F8FAFC' };
  return { label: 'Нет данных о сборе', color: '#64748B', background: '#F8FAFC' };
};

const formatDateTime = (value) => {
  if (!value) return 'Нет данных';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Нет данных' : parsed.toLocaleString('ru-RU');
};

const tileUrl = (zoom, x, y) =>
  `https://tile.openstreetmap.org/${zoom}/${x}/${y}.png`;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const clampZoom = (value) => clamp(value, 2, 18);

const normalizeCoordinate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const latLonToWorld = (lat, lon, zoom) => {
  const safeLat = clamp(lat, MIN_LAT, MAX_LAT);
  const safeLon = clamp(lon, MIN_LON, MAX_LON);
  const scale = TILE_SIZE * 2 ** zoom;
  const x = ((safeLon + 180) / 360) * scale;
  const sinLat = Math.sin((safeLat * Math.PI) / 180);
  const y =
    (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;
  return { x, y };
};

const getZoomForBounds = (points, width, height) => {
  if (!points.length) return 3;
  if (points.length === 1) return 15;

  for (let zoom = 16; zoom >= 2; zoom -= 1) {
    const world = points.map((point) => latLonToWorld(point.lat, point.lon, zoom));
    const xs = world.map((point) => point.x);
    const ys = world.map((point) => point.y);
    const spanX = Math.max(...xs) - Math.min(...xs);
    const spanY = Math.max(...ys) - Math.min(...ys);
    if (spanX <= width * 0.72 && spanY <= height * 0.68) {
      return zoom;
    }
  }
  return 2;
};

const zoomViewportAroundPoint = (viewport, nextZoom, anchorX, anchorY, size) => {
  const safeZoom = clampZoom(nextZoom);
  const prevScale = 2 ** viewport.zoom;
  const nextScale = 2 ** safeZoom;
  const worldX = viewport.centerX + (anchorX - size.width / 2);
  const worldY = viewport.centerY + (anchorY - size.height / 2);
  return {
    zoom: safeZoom,
    centerX: (worldX / prevScale) * nextScale - (anchorX - size.width / 2),
    centerY: (worldY / prevScale) * nextScale - (anchorY - size.height / 2),
  };
};

const formatStatChips = (marker) => {
  if (marker?.kind !== 'building') {
    const status = statusMeta(marker?.status);
    const type = markerMeta(marker?.kind);
    const chips = [
      { key: 'type', label: type.label, icon: <HubRoundedIcon sx={{ fontSize: 14 }} />, tone: type.color },
      { key: 'status', label: status.label, tone: status.color },
    ];
    if (marker?.devicesTotal) {
      chips.push({
        key: 'devices',
        label: `Приборов: ${marker.devicesTotal}`,
        icon: <SensorsRoundedIcon sx={{ fontSize: 14 }} />,
        tone: '#475569',
      });
    }
    if (marker?.devicesProblem) {
      chips.push({ key: 'problems', label: `Проблемных: ${marker.devicesProblem}`, tone: '#DC2626' });
    }
    if (marker?.kind === 'base_station' && marker?.coverageBuildings) {
      chips.push({ key: 'coverage', label: `Домов в зоне: ${marker.coverageBuildings}`, tone: '#047857' });
    }
    return chips;
  }
  const counts = marker?.stats || {};
  const chips = [];
  const total = Number(counts.total ?? marker?.deviceCount ?? 0);
  chips.push({ key: 'total', label: `Приборов: ${total}`, icon: <SensorsRoundedIcon sx={{ fontSize: 14 }} />, tone: BUILDING_COLOR });
  if (counts.hvs) chips.push({ key: 'hvs', label: `ХВС ${counts.hvs}`, icon: <WaterDropRoundedIcon sx={{ fontSize: 14 }} />, tone: '#0284C7' });
  if (counts.gvs) chips.push({ key: 'gvs', label: `ГВС ${counts.gvs}`, icon: <LocalFireDepartmentRoundedIcon sx={{ fontSize: 14 }} />, tone: '#EA580C' });
  if (counts.heating) chips.push({ key: 'heat', label: `Отопл. ${counts.heating}`, icon: <ThermostatRoundedIcon sx={{ fontSize: 14 }} />, tone: '#DC2626' });
  if (counts.heat_allocators) chips.push({ key: 'urt', label: `УРТ ${counts.heat_allocators}`, icon: <ThermostatRoundedIcon sx={{ fontSize: 14 }} />, tone: '#7C3AED' });
  if (counts.electricity) chips.push({ key: 'el', label: `Эл. ${counts.electricity}`, icon: <BoltRoundedIcon sx={{ fontSize: 14 }} />, tone: '#CA8A04' });
  if (marker?.coverageGateways) {
    chips.push({ key: 'coverage', label: `Доступно БС: ${marker.coverageGateways}`, icon: <CellTowerRoundedIcon sx={{ fontSize: 14 }} />, tone: '#047857' });
  }
  return chips;
};

function ObjectSummaryCard({ marker, onOpen, onClose }) {
  if (!marker) return null;
  const chips = formatStatChips(marker);
  const meta = markerMeta(marker.kind);
  const MetaIcon = meta.Icon;
  const isBuilding = marker.kind === 'building';
  return (
    <Paper
      elevation={0}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      sx={{
        width: 300,
        borderRadius: 3,
        border: `1px solid ${meta.color}38`,
        background: 'rgba(255,255,255,0.96)',
        boxShadow: '0 22px 48px rgba(15,23,42,0.18)',
        backdropFilter: 'blur(12px)',
        maxHeight: 340,
        overflowY: 'auto',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.15,
          background: `${meta.color}12`,
          borderBottom: '1px solid rgba(15,23,42,0.06)',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <MetaIcon sx={{ color: meta.color, fontSize: 21 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
            {marker.title || meta.label}
          </Typography>
        </Stack>
        {marker.subtitle && (
          <Typography variant="caption" color="text.secondary" display="block">
            {marker.subtitle}
          </Typography>
        )}
      </Box>
      <Stack spacing={1.1} sx={{ p: 1.35 }}>
        {marker.address && (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>
            {marker.address}
          </Typography>
        )}
        {!isBuilding && marker.host && (
          <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace' }}>
            {marker.host}
          </Typography>
        )}
        {!isBuilding && marker.lorawanGatewayId && (
          <Typography variant="caption" color="text.secondary">
            Gateway ID: {marker.lorawanGatewayId}
          </Typography>
        )}
        {!isBuilding && marker.coordinateSource === 'lorawan' && (
          <Typography variant="caption" color="text.secondary">
            Координаты получены от базовой станции
          </Typography>
        )}
        {!isBuilding && (
          <Typography variant="caption" color="text.secondary">
            Последняя связь: {formatDateTime(marker.lastSeenAt)}
          </Typography>
        )}
        {isBuilding && (() => {
          const reading = readingStatusMeta(marker.dataState);
          const progress = Math.max(0, Math.min(100, Number(marker.readingProgress || 0)));
          return (
            <Box
              sx={{
                p: 1.1,
                borderRadius: 2,
                border: `1px solid ${reading.color}24`,
                background: reading.background,
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                <Typography variant="caption" sx={{ color: reading.color, fontWeight: 800 }}>
                  {reading.label}
                </Typography>
                {!['unknown', 'no_devices', 'before_start'].includes(marker.dataState) && (
                  <Typography variant="caption" sx={{ color: reading.color, fontWeight: 800 }}>
                    {Math.round(progress)}%
                  </Typography>
                )}
              </Stack>
              {!['unknown', 'no_devices', 'before_start'].includes(marker.dataState) && (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ mt: 0.7, mb: 0.65, height: 6, borderRadius: 999, backgroundColor: `${reading.color}1F`, '& .MuiLinearProgress-bar': { backgroundColor: reading.color, borderRadius: 999 } }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Приборы: {marker.submittedDevices}/{marker.deviceCount}
                    {marker.totalApartments > 0
                      ? ` · Квартиры: ${marker.completedApartments}/${marker.totalApartments}`
                      : ''}
                  </Typography>
                </>
              )}
            </Box>
          );
        })()}
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {chips.map((chip) => (
            <Chip
              key={chip.key}
              size="small"
              icon={chip.icon}
              label={chip.label}
              sx={{
                height: 24,
                borderRadius: 999,
                background: `${chip.tone}14`,
                color: chip.tone,
                fontWeight: 600,
                '& .MuiChip-icon': { color: chip.tone },
              }}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            endIcon={<OpenInNewRoundedIcon />}
            onClick={() => onOpen?.(marker)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            {isBuilding ? 'Открыть дом' : 'Открыть шлюзы'}
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={onClose}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Скрыть
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function ObjectsMapView({
  markers = [],
  coverageLinks = [],
  selectedId = null,
  onSelect,
  onOpen,
  height = 560,
}) {
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const [size, setSize] = useState({ width: 900, height });
  const [viewport, setViewport] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [openGroupId, setOpenGroupId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect;
      if (!next) return;
      setSize({
        width: Math.max(320, Math.round(next.width)),
        height: Math.max(240, Math.round(next.height)),
      });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const points = useMemo(
    () =>
      markers
        .map((marker) => {
          const lat = normalizeCoordinate(marker.latitude);
          const lon = normalizeCoordinate(marker.longitude);
          if (lat === null || lon === null) return null;
          return { id: marker.id, marker, lat, lon };
        })
        .filter(Boolean),
    [markers]
  );

  const selectedMarker = useMemo(
    () => markers.find((item) => String(item.id) === String(selectedId || '')) || null,
    [markers, selectedId]
  );

  const baseViewport = useMemo(() => {
    const zoom = getZoomForBounds(points, size.width, size.height);
    if (!points.length) {
      const center = latLonToWorld(55.751244, 37.618423, zoom);
      return { zoom, centerX: center.x, centerY: center.y };
    }
    const world = points.map((point) => latLonToWorld(point.lat, point.lon, zoom));
    return {
      zoom,
      centerX: world.reduce((sum, point) => sum + point.x, 0) / world.length,
      centerY: world.reduce((sum, point) => sum + point.y, 0) / world.length,
    };
  }, [points, size.height, size.width]);

  useEffect(() => {
    setViewport(baseViewport);
  }, [baseViewport]);

  const activeViewport = viewport || baseViewport;

  const tiles = useMemo(() => {
    const { zoom, centerX, centerY } = activeViewport;
    const left = centerX - size.width / 2;
    const top = centerY - size.height / 2;
    const right = centerX + size.width / 2;
    const bottom = centerY + size.height / 2;
    const tileCount = 2 ** zoom;
    const result = [];
    for (let tileX = Math.floor(left / TILE_SIZE); tileX <= Math.floor(right / TILE_SIZE); tileX += 1) {
      for (let tileY = Math.floor(top / TILE_SIZE); tileY <= Math.floor(bottom / TILE_SIZE); tileY += 1) {
        if (tileY < 0 || tileY >= tileCount) continue;
        const wrappedX = ((tileX % tileCount) + tileCount) % tileCount;
        result.push({
          key: `${zoom}-${wrappedX}-${tileY}`,
          src: tileUrl(zoom, wrappedX, tileY),
          left: tileX * TILE_SIZE - left,
          top: tileY * TILE_SIZE - top,
        });
      }
    }
    return result;
  }, [activeViewport, size.height, size.width]);

  const markerPositions = useMemo(() => {
    const { zoom, centerX, centerY } = activeViewport;
    const left = centerX - size.width / 2;
    const top = centerY - size.height / 2;
    const grouped = new Map();
    points.forEach((point) => {
      const key = `${point.lat.toFixed(6)}:${point.lon.toFixed(6)}`;
      if (!grouped.has(key)) {
        grouped.set(key, { id: key, lat: point.lat, lon: point.lon, items: [] });
      }
      grouped.get(key).items.push(point.marker);
    });
    return Array.from(grouped.values()).map((group) => {
      const world = latLonToWorld(group.lat, group.lon, zoom);
      return { ...group, left: world.x - left, top: world.y - top };
    });
  }, [activeViewport, points, size.height, size.width]);

  const coverageGeometry = useMemo(() => {
    if (!coverageLinks.length) return [];
    const { zoom, centerX, centerY } = activeViewport;
    const left = centerX - size.width / 2;
    const top = centerY - size.height / 2;
    const positions = new Map();
    points.forEach((point) => {
      const world = latLonToWorld(point.lat, point.lon, zoom);
      positions.set(String(point.marker.id), { x: world.x - left, y: world.y - top });
    });
    const grouped = new Map();
    coverageLinks.forEach((link) => {
      const gatewayKey = `gateway:${link.gateway_id}`;
      const buildingKey = `building:${link.building_id}`;
      const gatewayPoint = positions.get(gatewayKey) || null;
      const buildingPoint = positions.get(buildingKey);
      if (!buildingPoint) return;
      if (!grouped.has(gatewayKey)) {
        grouped.set(gatewayKey, {
          gatewayKey,
          gatewayPoint,
          color: stableColor(link.gateway_id),
          links: [],
        });
      }
      const total = Math.max(1, Number(link.building_devices_total || 0));
      grouped.get(gatewayKey).links.push({
        ...link,
        buildingKey,
        buildingPoint,
        ratio: Math.min(1, Number(link.devices_heard || 0) / total),
        weakSignal:
          (link.rssi_avg != null && Number(link.rssi_avg) < -110) ||
          (link.snr_avg != null && Number(link.snr_avg) < -12),
      });
    });
    return Array.from(grouped.values()).map((group) => {
      const coveragePoints = [
        ...(group.gatewayPoint ? [group.gatewayPoint] : []),
        ...group.links.map((link) => link.buildingPoint),
      ];
      const hull = bufferedHull(coveragePoints);
      return { ...group, hull };
    });
  }, [activeViewport, coverageLinks, points, size.height, size.width]);

  const coverageHouseGeometry = useMemo(() => {
    if (!coverageLinks.length) return [];
    const { zoom, centerX, centerY } = activeViewport;
    const left = centerX - size.width / 2;
    const top = centerY - size.height / 2;
    const buildingPositions = new Map();
    points.forEach((point) => {
      if (point.marker.kind !== 'building') return;
      const world = latLonToWorld(point.lat, point.lon, zoom);
      buildingPositions.set(String(point.marker.buildingId), { x: world.x - left, y: world.y - top });
    });
    const grouped = new Map();
    coverageLinks.forEach((link) => {
      const buildingId = String(link.building_id);
      const point = buildingPositions.get(buildingId);
      if (!point) return;
      if (!grouped.has(buildingId)) grouped.set(buildingId, { buildingId, point, links: [] });
      const total = Math.max(1, Number(link.building_devices_total || 0));
      grouped.get(buildingId).links.push({
        gatewayId: String(link.gateway_id),
        color: stableColor(link.gateway_id),
        ratio: Math.min(1, Number(link.devices_heard || 0) / total),
        weakSignal:
          (link.rssi_avg != null && Number(link.rssi_avg) < -110) ||
          (link.snr_avg != null && Number(link.snr_avg) < -12),
      });
    });
    return Array.from(grouped.values());
  }, [activeViewport, coverageLinks, points, size.height, size.width]);

  const hasCoverageSelection = useMemo(
    () =>
      coverageGeometry.some(
        (group) =>
          String(selectedId || '') === group.gatewayKey ||
          group.links.some((link) => String(selectedId || '') === link.buildingKey)
      ) ||
      coverageHouseGeometry.some(
        (house) =>
          String(selectedId || '') === `building:${house.buildingId}` ||
          house.links.some((link) => String(selectedId || '') === `gateway:${link.gatewayId}`)
      ),
    [coverageGeometry, coverageHouseGeometry, selectedId]
  );

  const selectedScreenPos = useMemo(() => {
    if (!selectedMarker) return null;
    const lat = normalizeCoordinate(selectedMarker.latitude);
    const lon = normalizeCoordinate(selectedMarker.longitude);
    if (lat === null || lon === null) return null;
    const { zoom, centerX, centerY } = activeViewport;
    const world = latLonToWorld(lat, lon, zoom);
    return {
      left: world.x - (centerX - size.width / 2),
      top: world.y - (centerY - size.height / 2),
    };
  }, [activeViewport, selectedMarker, size.height, size.width]);

  const handleZoomStep = (delta) => {
    setViewport((current) =>
      zoomViewportAroundPoint(
        current || baseViewport,
        (current || baseViewport).zoom + delta,
        size.width / 2,
        size.height / 2,
        size
      )
    );
  };

  const stopMapEvent = (event) => event.stopPropagation();
  const stopMapPointerEvent = (event) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const handlePointerDown = (event) => {
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin: viewport || baseViewport,
      moved: false,
    };
    setIsDragging(false);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - dragRef.current.startX;
    const deltaY = event.clientY - dragRef.current.startY;
    dragRef.current.moved = dragRef.current.moved || Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3;
    if (dragRef.current.moved) setIsDragging(true);
    setViewport({
      ...dragRef.current.origin,
      centerX: dragRef.current.origin.centerX - deltaX,
      centerY: dragRef.current.origin.centerY - deltaY,
    });
  };

  const handlePointerFinish = (event) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    window.requestAnimationFrame(() => setIsDragging(false));
    dragRef.current = null;
  };

  const handleMapClick = () => {
    if (dragRef.current?.moved || isDragging) return;
    setOpenGroupId(null);
    onSelect?.(null);
  };

  const handleMarkerClick = (event, group) => {
    event.stopPropagation();
    if (group.items.length <= 1) {
      onSelect?.(group.items[0]);
      setOpenGroupId(null);
      return;
    }
    setOpenGroupId((current) => (current === group.id ? null : group.id));
  };

  const summaryLeft = selectedScreenPos
    ? clamp(selectedScreenPos.left + 18, 12, Math.max(12, size.width - 312))
    : 12;
  const summaryTop = selectedScreenPos
    ? clamp(selectedScreenPos.top - 250, 12, Math.max(12, size.height - 352))
    : 12;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        borderColor: 'rgba(15,23,42,0.08)',
        background: '#F8FAFC',
      }}
    >
      <Box
        ref={containerRef}
        onClick={handleMapClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerFinish}
        onPointerCancel={handlePointerFinish}
        onPointerLeave={handlePointerFinish}
        sx={{
          position: 'relative',
          width: '100%',
          height,
          overflow: 'hidden',
          cursor: isDragging ? 'grabbing' : 'grab',
          background: '#E5E7EB',
          touchAction: 'none',
          '@keyframes mapPinPulse': {
            '0%': { transform: 'translate(-50%, -50%) scale(0.7)', opacity: 0.55 },
            '70%': { transform: 'translate(-50%, -50%) scale(1.55)', opacity: 0 },
            '100%': { transform: 'translate(-50%, -50%) scale(1.55)', opacity: 0 },
          },
        }}
      >
        <Stack
          onClick={stopMapEvent}
          onPointerDown={stopMapPointerEvent}
          spacing={0.75}
          sx={{ position: 'absolute', top: 12, right: 12, zIndex: 6 }}
        >
          {[
            { icon: <AddRoundedIcon fontSize="small" />, onClick: () => handleZoomStep(1), contained: true },
            { icon: <RemoveRoundedIcon fontSize="small" />, onClick: () => handleZoomStep(-1), contained: true },
            {
              icon: <CenterFocusStrongRoundedIcon fontSize="small" />,
              onClick: () => setViewport(baseViewport),
              contained: false,
            },
          ].map((btn, index) => (
            <Button
              key={index}
              variant={btn.contained ? 'contained' : 'outlined'}
              size="small"
              onClick={btn.onClick}
              sx={{
                minWidth: 40,
                width: 40,
                height: 40,
                p: 0,
                borderRadius: 2.25,
                boxShadow: btn.contained ? '0 10px 24px rgba(15,23,42,0.14)' : 'none',
                background: btn.contained ? undefined : 'rgba(255,255,255,0.92)',
              }}
            >
              {btn.icon}
            </Button>
          ))}
        </Stack>

        {tiles.map((tile) => (
          <Box
            key={tile.key}
            component="img"
            src={tile.src}
            alt=""
            draggable={false}
            sx={{
              position: 'absolute',
              left: tile.left,
              top: tile.top,
              width: TILE_SIZE,
              height: TILE_SIZE,
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
        ))}

        {(coverageGeometry.length > 0 || coverageHouseGeometry.length > 0) && (
          <svg
            width={size.width}
            height={size.height}
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none' }}
          >
            {coverageHouseGeometry.map((house) => {
              const selected = String(selectedId || '') === `building:${house.buildingId}`;
              return (
                <g key={`house-coverage:${house.buildingId}`} opacity={selected || !hasCoverageSelection ? 1 : 0.2}>
                  {house.links.map((link, index) => (
                    <circle
                      key={`${house.buildingId}:${link.gatewayId}`}
                      cx={house.point.x}
                      cy={house.point.y}
                      r={17 + index * 4}
                      fill="none"
                      stroke={link.color}
                      strokeOpacity={selected ? 0.92 : 0.58}
                      strokeWidth={selected ? 3 : 2}
                      strokeDasharray={link.ratio < 0.5 || link.weakSignal ? '4 4' : undefined}
                    />
                  ))}
                </g>
              );
            })}
            {coverageGeometry.map((group) => {
              const groupActive =
                !hasCoverageSelection ||
                String(selectedId) === group.gatewayKey ||
                group.links.some((link) => String(selectedId) === link.buildingKey);
              const polygonPoints = group.hull.map((point) => `${point.x},${point.y}`).join(' ');
              return (
                <g key={group.gatewayKey} opacity={groupActive ? 1 : 0.16}>
                  {group.hull.length >= 3 && (
                    <polygon
                      points={polygonPoints}
                      fill={group.color}
                      fillOpacity="0.075"
                      stroke={group.color}
                      strokeOpacity="0.32"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                  )}
                  {group.links.map((link) => {
                    if (!group.gatewayPoint) return null;
                    const linkActive =
                      String(selectedId || '') === group.gatewayKey ||
                      String(selectedId || '') === link.buildingKey;
                    return (
                      <path
                        key={`${group.gatewayKey}:${link.buildingKey}`}
                        d={curvedPath(group.gatewayPoint, link.buildingPoint)}
                        fill="none"
                        stroke={group.color}
                        strokeOpacity={linkActive ? 0.82 : 0.24}
                        strokeWidth={linkActive ? 2.8 : 1 + link.ratio * 1.2}
                        strokeLinecap="round"
                        strokeDasharray={link.ratio < 0.5 || link.weakSignal ? '5 5' : undefined}
                      />
                    );
                  })}
                  {group.gatewayPoint && (
                    <circle
                      cx={group.gatewayPoint.x}
                      cy={group.gatewayPoint.y}
                      r="18"
                      fill={group.color}
                      fillOpacity="0.1"
                      stroke={group.color}
                      strokeOpacity="0.3"
                    />
                  )}
                </g>
              );
            })}
          </svg>
        )}

        {markerPositions.map((group) => {
          const isSelected = group.items.some(
            (item) => String(selectedId || '') === String(item.id)
          );
          const isHovered = group.items.some(
            (item) => String(hoveredId || '') === String(item.id)
          );
          const isMulti = group.items.length > 1;
          const kinds = new Set(group.items.map((item) => item.kind || 'building'));
          const kind = kinds.size > 1 ? 'mixed' : group.items[0]?.kind || 'building';
          const meta = markerMeta(kind);
          const PinIcon = meta.Icon;
          const buildingMarker = !isMulti && kind === 'building' ? group.items[0] : null;
          const reading = buildingMarker ? readingStatusMeta(buildingMarker.dataState) : null;

          return (
            <Box
              key={group.id}
              sx={{
                position: 'absolute',
                left: group.left,
                top: group.top,
                zIndex: isSelected || openGroupId === group.id ? 5 : isHovered ? 4 : 2,
              }}
            >
              {isSelected && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    border: `2px solid ${meta.color}88`,
                    background: `${meta.color}1F`,
                    transform: 'translate(-50%, -50%)',
                    animation: 'mapPinPulse 1.8s ease-out infinite',
                    pointerEvents: 'none',
                  }}
                />
              )}
              <Box
                data-map-marker="true"
                onPointerDown={stopMapPointerEvent}
                onClick={(event) => handleMarkerClick(event, group)}
                onMouseEnter={() => setHoveredId(group.items[0]?.id || null)}
                onMouseLeave={() => setHoveredId(null)}
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  transform: 'translate(-50%, -100%)',
                  cursor: 'pointer',
                  transition: 'transform 160ms ease',
                  ...(isHovered || isSelected
                    ? { transform: 'translate(-50%, -100%) scale(1.08)' }
                    : null),
                }}
              >
                <Box
                  sx={{
                    width: isSelected ? 34 : isHovered ? 32 : 30,
                    height: isSelected ? 34 : isHovered ? 32 : 30,
                    position: 'relative',
                    borderRadius: kind === 'base_station' ? '50%' : kind === 'uspd' ? 2 : 2.5,
                    background: meta.color,
                    border: '2px solid rgba(255,255,255,0.96)',
                    display: 'grid',
                    placeItems: 'center',
                    color: '#fff',
                    boxShadow: isSelected
                      ? `0 0 0 4px ${meta.color}38, 0 12px 24px rgba(15,23,42,0.28)`
                      : isHovered
                        ? `0 0 0 3px ${meta.color}2E, 0 10px 18px rgba(15,23,42,0.22)`
                        : '0 6px 16px rgba(15,23,42,0.18)',
                    transition: 'width 160ms ease, height 160ms ease, box-shadow 160ms ease',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: '50%',
                      bottom: -5,
                      width: 9,
                      height: 9,
                      background: meta.color,
                      transform: 'translateX(-50%) rotate(45deg)',
                      borderRight: '2px solid rgba(255,255,255,0.96)',
                      borderBottom: '2px solid rgba(255,255,255,0.96)',
                    },
                  }}
                >
                  <PinIcon sx={{ fontSize: isSelected ? 20 : 18 }} />
                  {reading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: -5,
                        bottom: -4,
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: reading.color,
                        border: '2px solid #fff',
                        boxShadow: '0 2px 6px rgba(15,23,42,0.24)',
                        zIndex: 2,
                      }}
                    />
                  )}
                </Box>
                {isMulti && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -10,
                      minWidth: 20,
                      height: 20,
                      px: 0.5,
                      borderRadius: 999,
                      background: '#0F172A',
                      color: '#fff',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {group.items.length}
                  </Box>
                )}
              </Box>

              {openGroupId === group.id && isMulti && (
                <Paper
                  variant="outlined"
                  onPointerDown={stopMapPointerEvent}
                  onClick={stopMapEvent}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 14,
                    width: 280,
                    borderRadius: 2.5,
                    background: 'rgba(255,255,255,0.96)',
                    boxShadow: '0 18px 40px rgba(15,23,42,0.16)',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ px: 1.25, py: 1, borderBottom: '1px solid rgba(15,23,42,0.06)' }}>
                    <Typography variant="caption" color="text.secondary">
                      В этой точке: {group.items.length}
                    </Typography>
                  </Box>
                  <Stack spacing={0.75} sx={{ p: 1, maxHeight: 260, overflowY: 'auto' }}>
                    {group.items.map((item) => (
                      <Button
                        key={item.id}
                        variant={String(selectedId) === String(item.id) ? 'contained' : 'text'}
                        size="small"
                        onClick={() => {
                          onSelect?.(item);
                          setOpenGroupId(null);
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          borderRadius: 2,
                          textTransform: 'none',
                          minHeight: 52,
                        }}
                      >
                        <Stack spacing={0.35} sx={{ minWidth: 0, width: '100%' }}>
                          <Stack direction="row" spacing={0.65} alignItems="center">
                            {React.createElement(markerMeta(item.kind).Icon, {
                              sx: { fontSize: 16, color: markerMeta(item.kind).color },
                            })}
                            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                              {item.title}
                            </Typography>
                            {item.kind === 'building' && (
                              <Box
                                title={readingStatusMeta(item.dataState).label}
                                sx={{
                                  ml: 'auto',
                                  width: 9,
                                  height: 9,
                                  flex: '0 0 auto',
                                  borderRadius: '50%',
                                  background: readingStatusMeta(item.dataState).color,
                                }}
                              />
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {item.address || item.subtitle || 'Адрес не указан'}
                          </Typography>
                        </Stack>
                      </Button>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Box>
          );
        })}

        {selectedMarker && selectedScreenPos && (
          <Box
            sx={{
              position: 'absolute',
              left: summaryLeft,
              top: summaryTop,
              zIndex: 7,
            }}
          >
            <ObjectSummaryCard
              marker={selectedMarker}
              onOpen={onOpen}
              onClose={() => onSelect?.(null)}
            />
          </Box>
        )}

        {!points.length && (
          <Stack
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(248,250,252,0.72)',
            }}
          >
            <PlaceRoundedIcon sx={{ color: 'text.secondary', fontSize: 32 }} />
            <Typography variant="body2" color="text.secondary">
              Нет объектов с координатами
            </Typography>
            <Chip size="small" label="Проверьте координаты объектов" />
          </Stack>
        )}
      </Box>
    </Paper>
  );
}
